import { parseBookingRequirements } from '../components/VenueDashboard/utils/bookingAddOns';
import { calculateDurationHours, isPastBookingDate } from '../components/VenueDashboard/utils/dateUtils';
import { getPerPersonRateFromAvailabilityPreview } from '../components/VenueDashboard/utils/bookingUtils';
import { roundMoney, resolveBookingBaseAmount } from './bookingAmounts';
import { bookingHeadcount } from './bookingHeadcount';
import {
  bookingHasListedSpaceRate,
  buildPricingMetadata,
  computeBaseFromPricingMetadata,
  isPerPersonBookingType,
  normalizeBookingModeApi,
  normalizeBookingTypeForUi,
  readPricingMetadata,
  resolveBookingTypeFromRecord,
  resolveConfirmedPerPersonRate,
  resolveConfirmedSpaceHourlyRate
} from './bookingPricingMetadata';

export { isPerPersonBookingType, normalizeBookingModeApi, normalizeBookingTypeForUi };

export const sumDraftPackageLines = (packages) => {
  if (!Array.isArray(packages) || packages.length === 0) return 0;
  return roundMoney(
    packages.reduce((sum, line) => sum + (Number(line?.total ?? line?.amount) || 0), 0)
  );
};

/** Whether an edit should trigger automatic price recalculation. */
export const shouldRecalculateBookingPrice = ({
  trigger,
  bookingAmountManuallyEdited,
  hasListedSpaceRate = true
}) => {
  if (bookingAmountManuallyEdited) return false;
  if (hasListedSpaceRate === false) return false;
  return trigger === 'headcount' || trigger === 'mode';
};

const parsePositiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const originalAttendeesFromBooking = (booking, req, fallback = 1) => {
  return bookingHeadcount(booking, req) ?? fallback;
};

/**
 * Recompute venue hire from stored pricing metadata.
 * Used when headcount or booking mode changes — never on date/time-only edits.
 */
export const computeBaseAmountFromStoredRates = ({
  booking,
  bookingType,
  attendees,
  startTime,
  endTime,
  currentBaseAmount
}) => {
  if (!booking) return null;

  const req = parseBookingRequirements(booking);
  const duration =
    calculateDurationHours(startTime, endTime) ||
    parsePositiveNumber(booking.duration_hours ?? req.duration_hours) ||
    0;
  if (duration <= 0) return null;

  const headcount = Math.max(1, Number.parseInt(attendees, 10) || 1);
  const pricingMode = normalizeBookingModeApi(bookingType);
  const originalAttendees = originalAttendeesFromBooking(booking, req, headcount);
  const stored = readPricingMetadata(booking, req);
  const isPast = isPastBookingDate(booking);
  const confirmedBase = parsePositiveNumber(
    req.base_fee ?? stored.baseFee ?? resolveBookingBaseAmount(booking)
  );

  let perPersonRate =
    pricingMode === 'per_person'
      ? resolveConfirmedPerPersonRate({
          booking,
          req,
          storedBaseAmount: confirmedBase,
          duration,
          originalHeadcount: originalAttendees,
          isPastBooking: isPast
        })
      : stored.perPersonRate;
  let spaceHourlyRate =
    pricingMode === 'exclusive'
      ? resolveConfirmedSpaceHourlyRate({
          booking,
          req,
          storedBaseAmount: confirmedBase,
          duration,
          isPastBooking: isPast
        })
      : stored.spaceHourlyRate;

  const computed = computeBaseFromPricingMetadata({
    pricingMode,
    headcount,
    duration,
    perPersonRate,
    spaceHourlyRate
  });
  if (computed != null) return computed;

  const storedBaseFallback = parsePositiveNumber(currentBaseAmount ?? stored.baseFee);
  return storedBaseFallback != null ? roundMoney(storedBaseFallback) : null;
};

/**
 * Best-effort venue hire amount for an in-progress booking edit.
 * Only recalculates when trigger is 'headcount' or 'mode'.
 * Date/time changes alone preserve the existing price.
 */
export const resolveEditedBookingBaseAmount = async ({
  venueDashboardApi,
  booking,
  venueId,
  bookingType,
  eventDate,
  startTime,
  endTime,
  attendees,
  currentBaseAmount,
  timezone,
  trigger = null,
  bookingAmountManuallyEdited = false
}) => {
  if (!shouldRecalculateBookingPrice({ trigger, bookingAmountManuallyEdited })) {
    const stored = parsePositiveNumber(currentBaseAmount);
    if (stored != null) return stored;
    const req = parseBookingRequirements(booking);
    const storedMeta = readPricingMetadata(booking, req);
    if (storedMeta.baseFee != null) return storedMeta.baseFee;
    return null;
  }

  const req = parseBookingRequirements(booking);
  if (!bookingHasListedSpaceRate(booking, req)) {
    const stored = parsePositiveNumber(currentBaseAmount);
    if (stored != null) return stored;
    const storedMeta = readPricingMetadata(booking, req);
    if (storedMeta.baseFee != null) return storedMeta.baseFee;
    return null;
  }
  const spaceId = booking?.space_id ?? req.space_id ?? null;
  const bookingMode = normalizeBookingModeApi(bookingType);
  const headcount = Math.max(1, Number.parseInt(attendees, 10) || 1);
  const duration = calculateDurationHours(startTime, endTime);
  const stored = readPricingMetadata(booking, req);
  const isPast = isPastBookingDate(booking);
  const originalAttendees = originalAttendeesFromBooking(booking, req, headcount);
  const confirmedBase = parsePositiveNumber(
    req.base_fee ?? stored.baseFee ?? resolveBookingBaseAmount(booking)
  );

  let perPersonRate =
    bookingMode === 'per_person'
      ? resolveConfirmedPerPersonRate({
          booking,
          req,
          storedBaseAmount: confirmedBase,
          duration,
          originalHeadcount: originalAttendees,
          isPastBooking: isPast
        })
      : stored.perPersonRate;
  let spaceHourlyRate =
    bookingMode === 'exclusive'
      ? resolveConfirmedSpaceHourlyRate({
          booking,
          req,
          storedBaseAmount: confirmedBase,
          duration,
          isPastBooking: isPast
        })
      : stored.spaceHourlyRate;

  // Fetch missing rates from availability preview only when mode/headcount changed
  // and no authoritative per-person / hourly rate is already stored on the booking.
  if (
    venueId &&
    spaceId &&
    eventDate &&
    startTime &&
    endTime &&
    duration > 0 &&
    ((bookingMode === 'per_person' && perPersonRate == null) ||
      (bookingMode === 'exclusive' && spaceHourlyRate == null))
  ) {
    try {
      const preview = await venueDashboardApi.getSpaceAvailabilityPreview(venueId, String(spaceId), {
        date: eventDate,
        startTime,
        endTime,
        bookingMode,
        headcount: bookingMode === 'per_person' ? headcount : undefined,
        timezone
      });

      if (preview && typeof preview === 'object') {
        if (bookingMode === 'per_person') {
          const previewRate = getPerPersonRateFromAvailabilityPreview(preview);
          const exclusiveHourly = Number(preview.hourly_rate);
          const tenPercentOfHourly = Number.isFinite(exclusiveHourly) ? exclusiveHourly * 0.1 : null;
          const previewIsHourlyStamp =
            previewRate != null &&
            tenPercentOfHourly != null &&
            tenPercentOfHourly > 0 &&
            Math.abs(previewRate - tenPercentOfHourly) < 0.02;
          if (previewRate != null && !previewIsHourlyStamp) perPersonRate = previewRate;
        } else if (preview.hourly_rate != null) {
          spaceHourlyRate = Number(preview.hourly_rate);
        }
      }
    } catch {
      // Fall back to stored rates below.
    }
  }

  const computed = computeBaseFromPricingMetadata({
    pricingMode: bookingMode,
    headcount,
    duration,
    perPersonRate,
    spaceHourlyRate
  });
  if (computed != null) return computed;

  return computeBaseAmountFromStoredRates({
    booking,
    bookingType,
    attendees,
    startTime,
    endTime,
    currentBaseAmount
  });
};

export { buildPricingMetadata, readPricingMetadata, resolveBookingTypeFromRecord };
